import { Link, useSearchParams } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { DEPARTMENTS, departmentStaff, resolveDepartment } from "../lib/departments";
import {
  getDeptCourses,
  SEMESTER_LABEL,
  COURSE_TYPE_LABEL,
} from "../lib/deptExtras";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function DepartmentCourses() {
  const [params] = useSearchParams();
  const code = params.get("dept") || "cs";
  const d = resolveDepartment(code);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!d) {
    return (
      <section className="center-hero">
        <div className="container">
          <div className="ch-eyebrow">404 · Courses</div>
          <h1 className="ch-name">Department not found.</h1>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>
            <Link to={ROUTES.academics} style={{ color: "var(--gold-400)" }}>← Back to academics</Link>
          </p>
        </div>
      </section>
    );
  }

  const courses = getDeptCourses(d.code);
  const bySem = courses.reduce<Record<number, typeof courses>>((acc, c) => {
    (acc[c.semester] = acc[c.semester] || []).push(c);
    return acc;
  }, {});
  const sems = Object.keys(bySem).map(Number).sort((a, b) => a - b);
  const totalCredits = courses.reduce((s, c) => s + c.credits, 0);
  const electives = courses.filter((c) => c.type === "elective" || c.type === "open-elective").length;
  const labs = courses.filter((c) => c.type === "lab").length;
  const others = DEPARTMENTS.filter((x) => x.slug !== d.slug).slice(0, 3);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={`B.E. · ${d.code}`}
        title={`${d.name} — Courses`}
        back={{ label: "Department", to: `${ROUTES.department}/${d.slug}` }}
      />

      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.academics}>Academics</Link>
            <span aria-hidden>/</span>
            <Link to={`${ROUTES.department}/${d.slug}`}>{d.code}</Link>
            <span aria-hidden>/</span>
            <span>Courses</span>
          </nav>
          <div className="ch-eyebrow">B.E. · {d.code} · 4 years · 8 semesters</div>
          <span className="ch-short">{courses.length} COURSES</span>
          <h1 className="ch-name">{d.name} — Courses.</h1>
          <p className="ch-summary">
            Semester-wise course list for the {d.code} programme. Codes follow the {d.code}<i>YXX</i> scheme — Y is the semester number, XX is the ordering within the semester. Semesters I and II are shared with all branches; III–VIII are branch-specific.
          </p>

          <div className="ch-stats">
            <div>
              <div className="num">{courses.length}</div>
              <div className="lbl">Courses</div>
            </div>
            <div>
              <div className="num">{totalCredits}</div>
              <div className="lbl">Total credits</div>
            </div>
            <div>
              <div className="num">{electives}</div>
              <div className="lbl">Electives</div>
            </div>
            <div>
              <div className="num">{labs}</div>
              <div className="lbl">Labs</div>
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              {sems.map((sem) => {
                const semCourses = bySem[sem];
                const semCredits = semCourses.reduce((s, c) => s + c.credits, 0);
                return (
                  <div key={sem} style={{ marginBottom: "var(--space-6)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                      <div className="kicker">Semester {SEMESTER_LABEL(sem)}</div>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.06em", color: "var(--text-muted)" }}>
                        {semCourses.length} courses · {semCredits} credits
                      </span>
                    </div>
                    <table className="programs-table">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Course</th>
                          <th style={{ textAlign: "right" }}>Credits</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {semCourses.map((c) => (
                          <tr key={c.code}>
                            <td className="ptype">{c.code}</td>
                            <td className="pname">{c.name}</td>
                            <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{c.credits}</td>
                            <td style={{ color: "var(--text-muted)" }}>{COURSE_TYPE_LABEL[c.type]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Department</div>
                <div className="dmc-value">
                  <Link to={`${ROUTES.department}/${d.slug}`}>{d.name}</Link>
                </div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Programme</div>
                <div className="dmc-value">B.E. · {d.code}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Total credits</div>
                <div className="dmc-value">{totalCredits}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Curriculum framework</div>
                <div className="dmc-value">
                  <Link to={ROUTES.curriculum}>Autonomous curriculum →</Link>
                </div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Calendar</div>
                <div className="dmc-value">
                  <Link to={ROUTES.academicCalendar}>Academic calendar →</Link>
                </div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Faculty</div>
                <div className="dmc-value">
                  <Link to={`${ROUTES.faculty}?dept=${d.staffCode ?? d.code}`}>{departmentStaff(d).length} members →</Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">Other branches</div>
              <h2>Course lists for sibling departments</h2>
            </div>
            <div className="er-grid">
              {others.map((o) => (
                <Link key={o.slug} to={`${ROUTES.deptCourses}?dept=${o.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{o.code}</span>
                    <span className="er-month">B.E.</span>
                  </div>
                  <div className="er-body">
                    <span className="er-tag">{o.level === "PG" ? "Postgraduate" : "Undergraduate"}</span>
                    <h3>{o.name}</h3>
                    <span className="er-venue">{o.name}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
